<template>
  <LoadingSpinner v-if="purchaseOrdersLoading || workOrdersLoading" />

  <div v-else class="create-bill">
    <PageHeader title="Add Bill" />

    <div class="mx-auto max-w-7xl">
      <BillForm
        :purchase-orders="purchaseOrders"
        :work-orders="workOrders"
        :save-function="createBill"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import BillForm from '../components/BillForm.vue'
import { Bill } from '@/models/bill'
import PageHeader from '@/components/app/AppPageHeader.vue'
import { useHead } from '@unhead/vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import PurchaseOrderService from '@/services/PurchaseOrderService'
import WorkOrderService from '@/services/WorkOrderService'
import ProjectService from '@/services/ProjectService'
import BillService from '@/services/BillService'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { useNotification } from '@kyvg/vue3-notification'
import { useRouter } from 'vue-router'

useHead({ title: 'Add Bill' })

const { notify } = useNotification()
const $router = useRouter()
const queryClient = useQueryClient()
const projectService = new ProjectService()
const purchaseOrderService = new PurchaseOrderService('')
const workOrderService = new WorkOrderService('')
const billService = new BillService()

const { data:purchaseOrders, isLoading:purchaseOrdersLoading } = useQuery({
  queryKey: ['purchaseOrders'],
  queryFn: async () => {
    const purchaseOrders = await purchaseOrderService.all()
    return Promise.all(purchaseOrders.map(async (purchaseOrder) => {
      const project = await projectService.find(purchaseOrder.projectId)
      return {
        value: purchaseOrder.id,
        label: `${purchaseOrder.purchaseOrderId} - ${project.name}`,
        vendorId: purchaseOrder.vendorId,
        projectId: purchaseOrder.projectId,
        items: purchaseOrder.items,
        purchaseOrderId: purchaseOrder.purchaseOrderId,
      }
    }))
  }
})

const { data:workOrders, isLoading:workOrdersLoading } = useQuery({
  queryKey: ['workOrders'],
  queryFn: async () => {
    const wOrders = await workOrderService.all()
    return Promise.all(wOrders.map(async (workOrder) => {
      const project = await projectService.find(workOrder.projectId)
      return {
        value: workOrder.id,
        label: `${workOrder.workOrderId} - ${project.name}`,
        subId: workOrder.subId,
        projectId: workOrder.projectId,
        items: workOrder.items,
        workOrderId: workOrder.workOrderId,
      }
    }))
  }
})

const { mutateAsync:createBill } = useMutation({
  mutationFn: (newBill: Bill) => billService.create(newBill),
  onSuccess: (createdBill: Bill) => {
    notify({
      type: 'success',
      title: 'Bill Created',
      text: 'Bill created successfully'
    })
    queryClient.setQueryData(['bill', createdBill.id], createdBill)
    queryClient.invalidateQueries({ queryKey: ['firestore','bills'], exact: false })
    $router.push({ name: 'ShowBill', params: { id: createdBill.id }, hash: '#showConfirmation' })
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Error',
      text: error.message || 'An error occurred while trying to create the bill'
    })
  }
})

</script>
